/**
 * datetime.ts — จัดกลุ่มวันด้วย Asia/Bangkok โดยรักษา source timestamp เดิม
 *
 * กติกา (CONTEXT.md §7, architecture.md §4.1):
 * - เก็บ source timestamp/timezone เดิม (ISO / TIMESTAMPTZ)
 * - "report date" = แปลงเป็น Asia/Bangkok แล้วตัดเป็นวัน (YYYY-MM-DD)
 * - Asia/Bangkok เป็น UTC+7 คงที่ (ไม่มี DST) แต่เราคำนวณผ่าน Intl เพื่อความถูกต้องทั่วไป
 */

export const REPORT_TIMEZONE = 'Asia/Bangkok';

/** สตริงวันที่รูปแบบ YYYY-MM-DD (report date key) */
export type DateKey = string;

const dateKeyFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: REPORT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function toDate(input: Date | string | number): Date {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) {
    throw new RangeError(`วันที่ไม่ถูกต้อง: ${String(input)}`);
  }
  return d;
}

/**
 * แปลง timestamp (ใด ๆ ที่มี tz/offset) เป็น report date key ตาม Asia/Bangkok
 * เช่น 2025-01-01T18:30:00Z → "2025-01-02" (เพราะ +07:00 ข้ามวัน)
 */
export function toReportDateKey(input: Date | string | number): DateKey {
  // en-CA ให้รูปแบบ YYYY-MM-DD อยู่แล้ว
  return dateKeyFormatter.format(toDate(input));
}

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: REPORT_TIMEZONE,
  weekday: 'short',
});

/** คืนชื่อวันในสัปดาห์ (Mon..Sun) ตาม Asia/Bangkok */
export function reportWeekday(input: Date | string | number): string {
  return WEEKDAY_FORMATTER.format(toDate(input));
}

/** ปี-เดือน (YYYY-MM) ตาม Asia/Bangkok — ใช้ group รายเดือน */
export function toReportMonthKey(input: Date | string | number): string {
  return toReportDateKey(input).slice(0, 7);
}

/** ปี (YYYY) ตาม Asia/Bangkok */
export function toReportYearKey(input: Date | string | number): string {
  return toReportDateKey(input).slice(0, 4);
}

/**
 * เลื่อน DateKey ไปตามจำนวนวัน (ใช้ UTC anchor ที่เที่ยงเพื่อเลี่ยงปัญหาขอบวัน)
 * คืน DateKey ใหม่
 */
export function shiftDateKey(key: DateKey, deltaDays: number): DateKey {
  const [y, m, d] = key.split('-').map((n) => Number.parseInt(n, 10));
  if (y === undefined || m === undefined || d === undefined) {
    throw new RangeError(`DateKey ไม่ถูกต้อง: ${key}`);
  }
  const anchor = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  anchor.setUTCDate(anchor.getUTCDate() + deltaDays);
  const yy = anchor.getUTCFullYear().toString().padStart(4, '0');
  const mm = (anchor.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = anchor.getUTCDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** จำนวนวันระหว่าง DateKey สองค่า (b - a) */
export function daysBetween(a: DateKey, b: DateKey): number {
  const toUtc = (key: DateKey): number => {
    const [y, m, d] = key.split('-').map((n) => Number.parseInt(n, 10));
    return Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1);
  };
  return Math.round((toUtc(b) - toUtc(a)) / 86_400_000);
}
